import { isPlatformBrowser } from '@angular/common';
import {
 DestroyRef,
 Injectable,
 PLATFORM_ID,
 TemplateRef,
 WritableSignal,
 inject,
 makeStateKey,
 signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TransferState } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
 GetAllVotesEcolodgePayloadModel,
 GetAllVotesPayloadModel,
} from '@core/models/domains/comments';
import { AuthService } from '@core/services/authentication/auth.service';
import { MomentService } from '@core/services/moment.service';
// import { OpentelemetryService } from '@core/services/opentelemetry.service';
import { HotelSummaryModel } from '@models/hotel';
import { ComboRooms, RoomInfo, RoomModel, RoomsApiModel } from '@models/room';
import { PDPRepository } from '@repos/pdp.repository';
import { AutocompleteService } from '@shared/services/autocomplete.service';
import { DatepickerService } from '@shared/services/datepicker.service';
import { SearchBoxService } from '@shared/services/serach-box.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Injectable()
export class HotelInfoUiService {
 hotelinfoRepo = inject(PDPRepository);
 searchBoxService = inject(SearchBoxService);
 datePickerService = inject(DatepickerService);
//  opentelemetryService = inject(OpentelemetryService);
 autocompleteService = inject(AutocompleteService);
 authService = inject(AuthService);
 momentService = inject(MomentService);
 platformid = inject(PLATFORM_ID);

 transferState = inject(TransferState);

 constructor() {}

 route = inject(ActivatedRoute);
 router = inject(Router);
 destroyRef = inject(DestroyRef);

 hotelFullName = '';
 hotelId = 0;
 hosueId = 0;
 nights = 0;
 ssrInit: WritableSignal<boolean> = signal(false);
 ecolodgTotalPrice: WritableSignal<any> = signal(0);
 lowestRoomPrice = 0;
 hotelInfo: WritableSignal<HotelSummaryModel> = signal({} as HotelSummaryModel);
 activeRoomFiltersIds: WritableSignal<Array<number>> = signal([]);
 activeRoomFiltersNames: WritableSignal<Array<string>> = signal([]);
 selectedRooms: WritableSignal<Array<RoomInfo & { count: number; id: number; name: string }>> =
  signal([]);
 ecolodgeRoomInfo: WritableSignal<Array<RoomInfo & { count: number; id: number; name: string }>> =
  signal([]);
 resetCountAll: WritableSignal<boolean | number> = signal(false); // this number could be -1(for remove all) or a roomId
 ExtraGuestCount: WritableSignal<number> = signal(0);
 FixedGuestCount: WritableSignal<number> = signal(0);
 resetCountSpecific: WritableSignal<boolean | number> = signal(false); // this number could be a roomId
 allRooms: WritableSignal<Array<RoomInfo[]>> = signal([]);
 comboRooms: WritableSignal<Array<ComboRooms>> = signal([]);
 selectedComboRooms: WritableSignal<Array<ComboRooms>> = signal([]);
 roomsLoading: WritableSignal<boolean> = signal(false);
 roomApiResponse$ = new BehaviorSubject<RoomsApiModel>({} as RoomsApiModel);

 getHotelInfoSummary(
  cityName: string,
  hotelName: string,
  date?: string,
  endDate?: string,
  accommodation?: string
 ): void | Observable<HotelSummaryModel> {
  // let [date, endDate] = this.searchBoxService.getStartAndEndDate()
  if (!date || !endDate) {
   [date, endDate] = this.searchBoxService.getStartAndEndDate();
  }

  const summaryKey = makeStateKey<HotelSummaryModel>('summary');
  const ssrInit = makeStateKey<HotelSummaryModel>('ssrInit');

  const summary$ = this.hotelinfoRepo
   .getHotelInfoSummary(cityName, hotelName, date, endDate, accommodation!)
   .pipe(
    tap((response: HotelSummaryModel) => {
     this.hotelInfo.set(response);
     this.hotelId = response.HotelId;
     this.hotelFullName = response.Name;
     this.setSearchDataOnInit(
      response.Name,
      response.SearchSection.StartDate,
      response.SearchSection.EndDate
     );
     this.transferState.set(summaryKey, response);
    })
   );

  if (!isPlatformBrowser(this.platformid)) {
   this.transferState.set(ssrInit, true);
   this.roomsLoading.set(true);
   return summary$;
  } else {
   const routeParamRefer = this.route.snapshot.queryParams['refer'];

   if (
    (routeParamRefer == 'home' ||
     !routeParamRefer ||
     routeParamRefer == 'searchresult' ||
     routeParamRefer == 'hotelinfo' ||
     routeParamRefer == 'iranlist') &&
    !this.transferState.get(ssrInit)
   ) {
    this.transferState.set(summaryKey, undefined);
   }
   const response = this.transferState.get(summaryKey);
   if (response) {
    this.hotelInfo.set(response);
    this.hotelId = response.HotelId;
    this.hotelFullName = response.Name;

    this.setSearchDataOnInit(
     response.Name,
     response.SearchSection.StartDate,
     response.SearchSection.EndDate
    );
    this.transferState.set(ssrInit, false);
   } else summary$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
 }

 calculateLowestRoomPrice(rooms: any) {
  let lowestPrice = rooms.room.roomContainer[0]?.roomInfos[0]?.roomPrice?.price?.ihoPrice;
  rooms.room.roomContainer.forEach((room: any) => {
   if (
    room.roomInfos[0].roomPrice?.price?.ihoPrice < lowestPrice &&
    room.roomInfos[0].roomPrice?.price?.ihoPrice != 0
   )
    lowestPrice = room?.roomInfos[0]?.roomPrice?.price?.ihoPrice;
  });
  this.lowestRoomPrice = lowestPrice;
 }

 setSearchDataOnInit(name: string, startDate: string, endDate: string) {
  this.searchBoxService.residentId = this.hotelId;
  this.searchBoxService.residentType = 3;
  this.searchBoxService.residentName = name;
  this.searchBoxService.dateRange = [startDate, endDate];
  this.datePickerService.setStartDate(startDate);
  this.datePickerService.setEndDate(endDate);
  this.autocompleteService.setSelectedItem({
   id: this.hotelId,
   type: 3,
   name,
  });
 }

 getRooms() {
  let [date, endDate] = this.searchBoxService.getStartAndEndDate();
  let queryParams = this.route.snapshot.queryParams;
  if (queryParams?.['date'] && queryParams?.['endDate']) {
   date = queryParams['date'];
   endDate = queryParams['endDate'];
  }
  this.roomsLoading.update(() => true);
  return this.hotelinfoRepo
   .getRooms(this.hotelId, date, endDate)
   .pipe(takeUntilDestroyed(this.destroyRef))
   .subscribe((res) => {
    this.calculateLowestRoomPrice(res);

    this.roomApiResponse$.next(res);
    this.selectedRooms.update(() => []);

    // if(res.room.combenations?.length) {
    this.selectedComboRooms.update(() => []);
    this.comboRooms.update(() => {
     res.room?.combenations?.[0]?.rooms?.forEach((each) => {
      each.priceInfo.checkInDate = this.momentService.convertISOtoJalali(
       each.priceInfo.checkInDate
      );
      each.priceInfo.checkOutDate = this.momentService.convertISOtoJalali(
       each.priceInfo.checkOutDate
      );
     });

     return res.room?.combenations;
    });
    // }

    this.nights =
     res.room.roomContainer?.[0]?.roomInfos[0]?.roomPrice?.price?.nights ??
     (this.datePickerService._selectedNights().length - 1 || this.hotelInfo().SearchSection.Night);
    if (res.room.roomContainer.length)
     this.allRooms.set(
      res.room.roomContainer.map((eachRoom: RoomModel) => [...eachRoom.roomInfos])
     );
    else this.allRooms.set([]);

    this.roomsLoading.set(false);
   });
 }

 getSimilarHotels(hotelId: number, hotelPrice: number) {
  const [date, endDate] = this.searchBoxService.getStartAndEndDate();
  // const dateParams = this.route.snapshot.queryParams
  // return this.hotelinfoRepo.getSimilarHotels(hotelId, dateParams['date'], dateParams['endDate'], hotelPrice)
  return this.hotelinfoRepo.getSimilarHotels(hotelId, date, endDate, hotelPrice);
 }

 getOtherRooms(parentId: number, type: number) {
  const [date, endDate] = this.searchBoxService.getStartAndEndDate();
  return this.hotelinfoRepo.getOtherRooms(parentId, date, endDate, type);
 }
 getSimilarAccommodation(hotelId: number, houseId: string, type: number) {
  const [date, endDate] = this.searchBoxService.getStartAndEndDate();
  return this.hotelinfoRepo.getSimilarAccommodation(hotelId, houseId, date, endDate, type);
 }

 getVoteSection(hid: number) {
  return this.hotelinfoRepo.getVoteSection(hid);
 }
 getVoteAll(model: GetAllVotesPayloadModel) {
  return this.hotelinfoRepo.getVotes(model);
 }
 getVoteAllEcolodge(model: GetAllVotesEcolodgePayloadModel) {
  return this.hotelinfoRepo.getVotesEcolodge(model);
 }
 getVoteFilters(hid: number) {
  return this.hotelinfoRepo.getVoteFilters(hid);
 }

 getGalleryHotel(hid: number) {
  return this.hotelinfoRepo.getGalleryInfo(hid);
 }
 getRoomDetails(model: any) {
  return this.hotelinfoRepo.getRoomDetails(model);
 }

 // method to increase the number of selected rooms by one
 increaseSelectedRooms(newRoom: RoomInfo, roomId: number, roomName: string, isCombo: boolean) {
  const rooms = this.selectedRooms();
  const foundItem = rooms.find((eachRoom) => eachRoom.id === roomId);
  const otherRooms = rooms.filter((each) => each.id !== roomId);
  if (foundItem) {
   this.selectedRooms.update(() => [
    ...otherRooms,
    { ...newRoom, count: foundItem.count + 1, id: roomId, name: roomName },
   ]);
  } else {
   this.selectedRooms.update(() => [
    ...otherRooms,
    { ...newRoom, count: 1, id: roomId, name: roomName },
   ]);
  }
  // this.opentelemetryService.startSpan('Room selection');
  // this.opentelemetryService.sendEvent('Room selection', {
  //  Session_Id: '',
  //  'mobile phone': this.authService._userData()?.mobile,
  //  First_track_id: '',
  //  Destination: this.autocompleteService._selectedItem()?.name,
  //  startDate: this.datePickerService._startDate(),
  //  endDate: this.datePickerService._endDate(),
  // });
  // this.opentelemetryService.endSpan();
 }

 // method to increase the number of selected rooms by one
 decreaseSelectedRooms(roomId: number, roomName: string, isCombo: boolean) {
  const rooms = this.selectedRooms();
  const foundItem = rooms.find((eachRoom) => eachRoom.id === roomId);

  if (!rooms.length || !foundItem) return;

  if (foundItem.count > 1) {
   const newRooms = rooms.filter((each) => each.id !== foundItem.id);
   this.selectedRooms.update(() => [
    ...newRooms,
    { ...foundItem, count: foundItem.count - 1, name: roomName },
   ]);
  } else this.selectedRooms.update(() => [...rooms.filter((each) => each.id !== foundItem.id)]);
  // this.opentelemetryService.startSpan('Room selection');
  // this.opentelemetryService.sendEvent('Room selection', {
  //  Session_Id: '',
  //  'mobile phone': this.authService._userData()?.mobile,
  //  First_track_id: '',
  //  Destination: this.autocompleteService._selectedItem()?.name,
  //  startDate: this.datePickerService._startDate(),
  //  endDate: this.datePickerService._endDate(),
  // });
  // this.opentelemetryService.endSpan();
 }

 addComboRoomsToSelection(comboRoomsId?: string) {
  const comboRooms = this.comboRooms().find((room) => room.combenationId === comboRoomsId);
  if (comboRooms && comboRoomsId) this.selectedComboRooms.update(() => [comboRooms]);
 }

 removeComboRoomsSelection(comboRoomsId?: string) {
  this.selectedComboRooms.update(() => []);
 }

 addToFavorite() {
  return this.hotelinfoRepo.addToFavorite(this.hotelId);
 }

 setPackage(startDate: string, endDate: string) {
  this.datePickerService.setStartDate(startDate);
  this.datePickerService.setEndDate(endDate);
  this.router
   .navigate([], {
    queryParams: {
     date: startDate,
     endDate: endDate,
    },
    queryParamsHandling: 'merge',
    relativeTo: this.route,
    preserveFragment: true,
   })
   .finally(() => this.getRooms());
 }

 getReserveUUID(reserveData: Record<string, any>) {
  return this.hotelinfoRepo.getReserveUUID(reserveData);
 }

 getEcolodgeCalenderDetails(
  range: { StartDate: string; EndDate: string },
  accommodationData: { hotelId: string; houseId: string; type: string },
  isFirstRequest: boolean = false
 ) {
  return this.hotelinfoRepo.getCalenderDetails(range, accommodationData, isFirstRequest);
 }
}
